export const CUSTOM_CSS_ATTR = 'data-custom-css';
export const CUSTOM_CSS_KEYS_ATTR = 'data-custom-css-keys';

const syncingCustomCss = new Set();

export function getEditorEm(editorOrEm) {
  if (!editorOrEm) return null;
  if (typeof editorOrEm.get === 'function' && editorOrEm.get('StyleManager')) {
    return editorOrEm;
  }
  return editorOrEm.em || editorOrEm;
}

export function resolveEditor(editorOrEm) {
  if (!editorOrEm) return null;
  if (typeof editorOrEm.getSelected === 'function' && editorOrEm.StyleManager) {
    return editorOrEm;
  }
  const em = getEditorEm(editorOrEm);
  return em?.__customCssEditor || editorOrEm;
}

export function getCustomCssState(editorOrEm) {
  const em = getEditorEm(editorOrEm);
  return em?.__customCssState || null;
}

/**
 * Parse a block of CSS declarations (e.g. "color: red; margin: 10px") into a style object.
 */
export function parseCustomCss(cssText) {
  const result = {};
  if (!cssText || typeof cssText !== 'string') return result;

  const withoutComments = cssText.replace(/\/\*[\s\S]*?\*\//g, '');
  withoutComments.split(';').forEach((block) => {
    const trimmed = block.trim();
    if (!trimmed) return;
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) return;
    const prop = trimmed.slice(0, colonIdx).trim();
    const value = trimmed.slice(colonIdx + 1).trim();
    if (prop) result[prop] = value;
  });

  return result;
}

function isComponentModel(model) {
  return Boolean(
    model &&
      typeof model === 'object' &&
      typeof model.getAttributes === 'function' &&
      typeof model.addAttributes === 'function'
  );
}

function getStoredCustomCssKeys(component) {
  const attrs = component?.getAttributes?.() || {};
  try {
    return JSON.parse(attrs[CUSTOM_CSS_KEYS_ATTR] || '[]');
  } catch {
    return [];
  }
}

export function safeGetSelected(editor) {
  if (!editor?.getSelected) return null;
  try {
    return editor.getSelected() || null;
  } catch {
    return null;
  }
}

function safeGetSelectedAll(editor) {
  if (!editor?.getSelectedAll) return [];
  try {
    const all = editor.getSelectedAll();
    return Array.isArray(all) ? all : [];
  } catch {
    return [];
  }
}

function safeGetSelectedToStyle(editor) {
  if (!editor?.getSelectedToStyle) return null;
  try {
    return editor.getSelectedToStyle() || null;
  } catch {
    return null;
  }
}

function safeSmGetComponent(editor) {
  try {
    const fromSm = editor?.StyleManager?.model?.get?.('component');
    return isComponentModel(fromSm) ? fromSm : null;
  } catch {
    return null;
  }
}

function safeSmGetSelected(editor) {
  try {
    const fromSm = editor?.StyleManager?.getSelected?.();
    return fromSm && typeof fromSm.addStyle === 'function' ? fromSm : null;
  } catch {
    return null;
  }
}

function safeSmGetModelToStyle(editor, component) {
  if (!component) return null;
  try {
    const sm = editor?.StyleManager;
    if (!sm?.getModelToStyle) return null;
    const model = sm.getModelToStyle(component);
    return model && typeof model.addStyle === 'function' ? model : null;
  } catch {
    return null;
  }
}

function describeModel(model) {
  if (!model) return null;
  try {
    const attrs = typeof model.getAttributes === 'function' ? model.getAttributes() : {};
    return {
      cid: model.cid,
      id: attrs?.id || model.getId?.(),
      type: model.get?.('type'),
      tagName: model.get?.('tagName'),
      hasAddStyle: typeof model.addStyle === 'function',
      hasAddAttributes: typeof model.addAttributes === 'function',
    };
  } catch (err) {
    return { error: String(err) };
  }
}

export function logCustomCssApplyDebug(editor, cssText, result = {}) {
  const tracked = getCustomCssState(editor) || {};
  const debug = {
    result,
    cssText: (cssText || '').trim(),
    tracked: {
      component: describeModel(tracked.component),
      styleTarget: describeModel(tracked.styleTarget),
    },
    resolved: {
      component: describeModel(resolveComponentFromEditor(editor)),
      styleTarget: describeModel(
        resolveStyleTargetFromEditor(editor, resolveComponentFromEditor(editor))
      ),
    },
    sources: {
      getSelected: describeModel(safeGetSelected(editor)),
      getSelectedAll: safeGetSelectedAll(editor).map(describeModel),
      smComponent: describeModel(safeSmGetComponent(editor)),
      smSelected: describeModel(safeSmGetSelected(editor)),
      getSelectedToStyle: describeModel(safeGetSelectedToStyle(editor)),
    },
  };

  console.log('[custom-css] apply', debug);
  return debug;
}

function resolveComponentFromEditor(editor) {
  const tracked = getCustomCssState(editor)?.component;
  if (isComponentModel(tracked)) return tracked;

  const selected = safeGetSelected(editor);
  if (isComponentModel(selected)) return selected;

  const selectedAll = safeGetSelectedAll(editor);
  if (selectedAll.length) {
    const last = selectedAll[selectedAll.length - 1];
    if (isComponentModel(last)) return last;
  }

  const fromSm = safeSmGetComponent(editor);
  if (fromSm) return fromSm;

  return null;
}

function resolveStyleTargetFromEditor(editor, component) {
  const tracked = getCustomCssState(editor)?.styleTarget;
  if (tracked && typeof tracked.addStyle === 'function') return tracked;

  const fromSm = safeSmGetSelected(editor);
  if (fromSm) return fromSm;

  const toStyle = safeGetSelectedToStyle(editor);
  if (toStyle) return toStyle;

  const modelToStyle = safeSmGetModelToStyle(editor, component);
  if (modelToStyle) return modelToStyle;

  if (component && typeof component.addStyle === 'function') return component;

  return null;
}

/**
 * Keep the last selected component/target on the editor so sidebar controls
 * can still apply styles after focus moves into the Style Manager.
 */
export function initCustomCssSelectionTracking(editor) {
  const em = getEditorEm(editor);
  if (!em) return;

  em.__customCssEditor = resolveEditor(editor);
  if (!em.__customCssState) {
    em.__customCssState = { component: null, styleTarget: null };
  }

  if (em.__customCssTracking) return;
  em.__customCssTracking = true;

  const sync = (componentFromEvent) => {
    if (!isComponentModel(componentFromEvent)) return;

    const ed = resolveEditor(em);
    const state = em.__customCssState;
    state.component = componentFromEvent;
    state.styleTarget = resolveStyleTargetFromEditor(ed, componentFromEvent);

    console.warn('[custom-css] component:selected', {
      component: describeModel(componentFromEvent),
      styleTarget: describeModel(state.styleTarget),
    });
  };

  em.on('component:selected', sync);
}

/**
 * The component model (for attributes/metadata).
 */
export function getCustomCssComponent(editor) {
  return resolveComponentFromEditor(editor);
}

/**
 * The style target Style Manager actually renders (often a #id CSS rule, not the component).
 */
export function getCustomCssStyleTarget(editor) {
  const component = resolveComponentFromEditor(editor);
  return resolveStyleTargetFromEditor(editor, component);
}

function applyStylesToTarget(target, cssText, prevKeys) {
  if (!target || typeof target.addStyle !== 'function') return [];

  const clearObj = {};
  prevKeys.forEach((key) => {
    clearObj[key] = '';
  });
  if (prevKeys.length) {
    target.addStyle(clearObj);
  }

  const newStyles = parseCustomCss(cssText);
  const newKeys = Object.keys(newStyles);
  if (newKeys.length) {
    target.addStyle(newStyles);
  }

  return newKeys;
}

/**
 * Apply custom CSS to the active Style Manager target and persist text on the component.
 */
export function setCustomCssOnEditorTarget(editorOrEm, cssText) {
  const editor = resolveEditor(editorOrEm);
  if (!editor) {
    console.warn('[custom-css] apply failed: no editor');
    return false;
  }

  const component = resolveComponentFromEditor(editor);
  if (!isComponentModel(component)) {
    logCustomCssApplyDebug(editor, cssText, {
      success: false,
      reason: 'no-component',
    });
    return false;
  }

  const target = resolveStyleTargetFromEditor(editor, component);
  if (!target) {
    logCustomCssApplyDebug(editor, cssText, {
      success: false,
      reason: 'no-style-target',
      component: describeModel(component),
    });
    return false;
  }

  const guardKey = component.cid || component;
  if (syncingCustomCss.has(guardKey)) {
    logCustomCssApplyDebug(editor, cssText, {
      success: true,
      skipped: true,
      reason: 'already-syncing',
      component: describeModel(component),
      target: describeModel(target),
    });
    return true;
  }
  syncingCustomCss.add(guardKey);

  try {
    const trimmed = (cssText || '').trim();
    const prevKeys = getStoredCustomCssKeys(component);
    const newKeys = applyStylesToTarget(target, trimmed, prevKeys);

    component.addAttributes({
      [CUSTOM_CSS_ATTR]: trimmed,
      [CUSTOM_CSS_KEYS_ATTR]: JSON.stringify(newKeys),
    });

    logCustomCssApplyDebug(editor, cssText, {
      success: true,
      component: describeModel(component),
      target: describeModel(target),
      parsedKeys: newKeys,
      prevKeys,
    });

    return true;
  } catch (err) {
    logCustomCssApplyDebug(editor, cssText, {
      success: false,
      reason: 'exception',
      error: String(err),
      component: describeModel(component),
      target: describeModel(target),
    });
    throw err;
  } finally {
    syncingCustomCss.delete(guardKey);
  }
}

export function syncCustomCssFromAttr(component, editor) {
  if (!isComponentModel(component)) return;
  const attrs = component.getAttributes?.() || {};
  const cssText = attrs[CUSTOM_CSS_ATTR] || '';
  const prevKeys = getStoredCustomCssKeys(component);
  if (!cssText && !prevKeys.length) return;

  const target = safeSmGetModelToStyle(editor, component) || component;
  applyStylesToTarget(target, cssText, prevKeys);
}

export function syncCustomCssRecursive(component, editor) {
  if (!component) return;
  syncCustomCssFromAttr(component, editor);
  const children = component.get('components');
  if (children && children.length > 0) {
    children.each((child) => syncCustomCssRecursive(child, editor));
  }
}

export function getCustomCssText(component) {
  if (!isComponentModel(component)) return '';
  const attrs = component.getAttributes() || {};
  return attrs[CUSTOM_CSS_ATTR] || '';
}
